import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import profesorParticularApiService from '../services/ProfesorParticularApiService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import { useAuth } from '../contexts/AuthContext';
import { isAdminColegio } from '../utils/adminMenu';
import ParticularCuentaFields, {
  EMPTY_PARTICULAR_FORM,
  buildParticularPayload,
  formatCuit,
  validateParticularForm
} from './common/ParticularCuentaFields';

const AdminCreateProfesorParticular = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isGeneralAdmin = !isAdminColegio(user);

  const [form, setForm] = useState(EMPTY_PARTICULAR_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [particulares, setParticulares] = useState([]);
  const [particularesLoading, setParticularesLoading] = useState(false);
  const [particularesError, setParticularesError] = useState('');

  useEffect(() => {
    if (!isGeneralAdmin) {
      navigate('/admin/select-colegio', { replace: true });
    }
  }, [isGeneralAdmin, navigate]);

  useEffect(() => {
    const fetchParticulares = async () => {
      setParticularesLoading(true);
      setParticularesError('');
      try {
        const resp = await profesorParticularApiService.getProfesoresParticulares();
        const list = resp?.profesoresParticulares || resp?.ProfesoresParticulares || resp?.data || resp || [];
        setParticulares(Array.isArray(list) ? list : []);
      } catch (error) {
        setParticularesError(error.message || 'No pudimos cargar los particulares.');
      } finally {
        setParticularesLoading(false);
      }
    };

    if (isGeneralAdmin) {
      fetchParticulares();
    }
  }, [isGeneralAdmin]);

  const handleChange = (field, value) => {
    const nextValue = field === 'cuit' ? formatCuit(value) : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateParticularForm(form);
    if (validationError) {
      setMessage(validationError);
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando cuenta...');
    setMessageType('info');

    try {
      await profesorParticularApiService.createProfesorParticular(buildParticularPayload(form));
      setMessage('Cuenta de particular creada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el particular.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-standalone">
      <header className="admin-standalone-header">
        <div>
          <p className="eyebrow">Administración general</p>
          <h1>Alta particular</h1>
          <p className="subtitle">Crea la cuenta del particular: su institución (opcional) y el administrador que la gestiona.</p>
        </div>
        <div className="admin-standalone-actions">
          <ActiveColegioBadge />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}
          >
            <i className="fas fa-arrow-left" />
            <span>Volver a particulares</span>
          </button>
          <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
        </div>
      </header>

      <div className="admin-dual-panel">
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-user-tie" />
              <span>Datos de la cuenta</span>
            </div>
          </div>
          <div className="card-content">
            <form onSubmit={handleSubmit} className="prof-create-form">
              <ParticularCuentaFields form={form} onChange={handleChange} />

              {message && (
                <div
                  className="modal-message"
                  style={{
                    marginTop: '0.5rem',
                    borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                    background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                    color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                  }}
                >
                  {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                  {message}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                  <span>Crear cuenta</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-users" />
              <span>Particulares existentes</span>
            </div>
          </div>
          <div className="card-content">
            {particularesLoading && <p className="state">Cargando particulares...</p>}
            {particularesError && <p className="state error">{particularesError}</p>}
            {!particularesLoading && !particularesError && particulares.length === 0 && (
              <p className="state">Todavía no hay particulares creados.</p>
            )}
            {!particularesLoading && !particularesError && particulares.length > 0 && (
              <div className="admin-list">
                {particulares.map((reg) => (
                  <div key={reg.id || reg.Id || reg.email || reg.Email} className="admin-list-item">
                    <div>
                      <strong>{reg.nombreVisible || reg.NombreVisible || reg.nombre || reg.Nombre || 'Sin nombre'}</strong>
                      <p style={{ margin: '0.2rem 0 0', color: '#64748b' }}>
                        {(reg.institucionNombre || reg.InstitucionNombre) ? `Administrador: ${reg.nombre || reg.Nombre}` : (reg.email || reg.Email || '-')}
                      </p>
                    </div>
                    <span className={`status-chip ${(reg.activo ?? reg.Activo ?? true) ? 'success' : 'danger'}`}>
                      {(reg.activo ?? reg.Activo ?? true) ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateProfesorParticular;
